/**
 * Xero Import Form Component
 *
 * Drag-and-drop JSON file upload for importing Xero invoice data.
 */

import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type { ImportResponse } from '../../types'
import { useXeroImportForm } from './xero-import-form.hook'
import {
  XeroDropZone,
  XeroActionButtons,
  XeroValidationPanel,
  XeroErrorAlert,
  XeroImportResultPanel,
  XeroFormatHelp,
} from './xero-import-form.parts'

interface XeroImportFormProps {
  onSuccess?: (result: ImportResponse) => void
  onError?: (error: Error) => void
  onCreateOrganisation?: (xeroOrganisationId: string) => void
}

export function XeroImportForm({
  onSuccess,
  onError,
  onCreateOrganisation,
}: XeroImportFormProps): ReactElement {
  const { t } = useTranslation('admin')
  const ctrl = useXeroImportForm({ onSuccess, onError })
  const handleValidateClick = (): void => {
    void ctrl.handleValidate()
  }
  const handleUploadClick = (): void => {
    void ctrl.handleUpload()
  }
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-6 shadow-sm dark:border-stone-700 dark:bg-stone-800">
      <h3 className="mb-4 text-lg font-semibold text-stone-900 dark:text-white">
        {t('licensing.xero.importInvoices')}
      </h3>
      <p className="mb-4 text-sm text-stone-600 dark:text-stone-400">
        {t('licensing.xero.importInvoicesDescription')}
      </p>
      <XeroDropZone
        selectedFile={ctrl.selectedFile}
        isDragging={ctrl.isDragging}
        fileInputRef={ctrl.fileInputRef}
        onDragOver={ctrl.handleDragOver}
        onDragLeave={ctrl.handleDragLeave}
        onDrop={ctrl.handleDrop}
        onFileSelect={ctrl.handleFileSelect}
      />
      <XeroActionButtons
        selectedFile={ctrl.selectedFile}
        isValidating={ctrl.isValidating}
        isUploading={ctrl.isUploading}
        validationResult={ctrl.validationResult}
        onValidate={handleValidateClick}
        onUpload={handleUploadClick}
        onClear={ctrl.handleClear}
      />
      {ctrl.validationResult && (
        <XeroValidationPanel
          validationResult={ctrl.validationResult}
          onCreateOrganisation={onCreateOrganisation}
        />
      )}
      {ctrl.error && <XeroErrorAlert error={ctrl.error} />}
      {ctrl.result && <XeroImportResultPanel result={ctrl.result} />}
      <XeroFormatHelp />
    </div>
  )
}
